import { FormEvent, ChangeEvent } from 'react';

interface FormData {
	name: string;
	email: string;
	timeline: string;
	message: string;
	package: string;
}

interface ContactFormProps {
	formData: FormData;
	onSubmit: (e: FormEvent) => void;
	onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
	isSubmitting: boolean;
	isSuccess: boolean;
}

const ContactForm = ({ formData, onSubmit, onChange, isSubmitting, isSuccess }: ContactFormProps) => {
	if (isSuccess) {
		return (
			<div className="max-w-3xl mx-auto px-4 py-16 text-center">
				<div className="bg-indigo-600/20 rounded-2xl p-8 backdrop-blur-sm">
					<svg 
						className="w-16 h-16 text-green-500 mx-auto mb-6" 
						fill="none" 
						stroke="currentColor" 
						viewBox="0 0 24 24"
					>
						<path 
							strokeLinecap="round" 
							strokeLinejoin="round" 
							strokeWidth="2" 
							d="M5 13l4 4L19 7"
						/>
					</svg>
					<h2 className="text-3xl font-bold mb-4">Thank You!</h2>
					<p className="text-xl text-gray-300 mb-6">
						We've received your message and will get back to you soon.
					</p>
					<p className="text-gray-400">
						Our team typically responds within 24 hours.
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="max-w-3xl mx-auto px-4">
			<form 
				id="contact-form"
				onSubmit={onSubmit} 
				className="space-y-6"
			>
				<div>
					<label htmlFor="name" className="block text-sm font-medium mb-2">
						Name
					</label>
					<input
						type="text"
						id="name"
						name="name"
						value={formData.name}
						onChange={onChange}
						className="form-input w-full"
						required
						disabled={isSubmitting}
					/>
				</div>

				<div>
					<label htmlFor="email" className="block text-sm font-medium mb-2">
						Email
					</label>
					<input
						type="email"
						id="email"
						name="email"
						value={formData.email}
						onChange={onChange}
						className="form-input w-full"
						required
						disabled={isSubmitting}
					/>
				</div>

				<div>
					<label className="block text-sm font-medium mb-4">
						Project Timeline
					</label>
					<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
						{[
							{ value: '1 day', label: '1 Day MVP', icon: '🚀' },
							{ value: '1 week', label: '1 Week MVP', icon: '⚡' },
							{ value: '2 weeks', label: 'Enterprise MVP', icon: '💎' }
						].map((option) => (
							<label
								key={option.value}
								className={`
									relative flex items-center justify-center p-4 rounded-lg cursor-pointer
									transition-all duration-200
									${formData.timeline === option.value
										? 'bg-indigo-600 ring-2 ring-indigo-400 ring-offset-2 ring-offset-gray-900'
										: 'bg-white/5 hover:bg-white/10'
									}
									${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}
								`}
							>
								<input
									type="radio"
									name="timeline"
									value={option.value}
									checked={formData.timeline === option.value}
									onChange={onChange}
									className="sr-only"
									disabled={isSubmitting}
								/>
								<div className="text-center">
									<span className="text-2xl mb-2 block">{option.icon}</span>
									<span className="font-medium block">{option.label}</span>
								</div>
							</label>
						))}
					</div>
				</div>

				<div>
					<label htmlFor="message" className="block text-sm font-medium mb-2">
						Message
					</label>
					<textarea
						id="message"
						name="message"
						value={formData.message}
						onChange={onChange}
						className="form-input w-full h-32"
						required
						disabled={isSubmitting}
					/>
				</div>

				<button
					type="submit"
					className={`
						btn w-full
						${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}
					`}
					disabled={isSubmitting}
				>
					{isSubmitting ? 'Sending...' : 'Send Message'}
				</button>
			</form>
		</div>
	);
};

export default ContactForm;
